// web/src/components/records/TransferOwnershipDialog.tsx — the metadata-only
// "Transfer ownership" consequence dialog (content-guide §5.5, R15 / M4.8).
//
// Ownership is accountability, NOT access. The transfer flips owner_id and emits an
// `ownership.transferred` audit event — it never re-encrypts or moves a shard. The
// dialog states that plainly, requires picking a current member, and takes an
// explicit acknowledgement before the primary button enables.
import React, { useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import Modal from '../Modal';
import { Icon } from '../ui/Icon';
import { FeedbackMessage } from '../ui';
import type { RecordMemberView } from '../../../../shared/types';

export interface TransferCandidate {
  /** The member id to pass to api.transferOwnership(secretId, toOwnerId). */
  id: string;
  label: string;
}

/** Build the candidate list from a roster, excluding the current owner + revoked slots. */
export function candidatesFrom(
  members: RecordMemberView[],
  currentOwnerId: string,
): TransferCandidate[] {
  return members
    .filter((m) => !m.shardRevoked && m.memberId !== currentOwnerId)
    .map((m) => ({ id: m.memberId, label: m.email }));
}

export function TransferOwnershipDialog({
  recordName,
  candidates,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  recordName: string;
  candidates: TransferCandidate[];
  busy: boolean;
  error: string | null;
  onConfirm: (toOwnerId: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [toId, setToId] = useState<string>(candidates[0]?.id ?? '');
  const [ack, setAck] = useState(false);
  const newOwner = candidates.find((c) => c.id === toId);
  const canTransfer = !!toId && ack && !busy;

  if (candidates.length === 0) {
    return (
      <Modal title="No one to transfer to" maxWidth={420} onClose={onCancel}>
        <p style={{ color: 'var(--color-text2)', margin: 0, lineHeight: 1.5 }}>
          You can only transfer ownership to a current member of this record. Add a member
          first, then transfer.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Transfer ownership${newOwner ? ` to ${newOwner.label}` : ''}`}
      subtitle="Accountability, not access"
      maxWidth={480}
      dismissable={!busy}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(toId)}
            disabled={!canTransfer}
          >
            {busy ? (
              <span className="spinner" aria-hidden="true" />
            ) : (
              <Icon icon={ArrowRightLeft} size="sm" />
            )}
            Transfer ownership
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--color-text2)', marginTop: 0, lineHeight: 1.5 }}>
        Ownership is about <strong style={{ color: 'var(--color-text)' }}>accountability</strong>,
        not access. Transferring makes {newOwner?.label ?? 'the new owner'} the responsible
        party for <strong style={{ color: 'var(--color-text)' }}>{recordName}</strong> — reporting,
        possession proofs, and who to chase. It does not move or re-encrypt any shard, and it
        does not let them read the secret.
      </p>

      <label className="field-label" htmlFor="to-owner">
        New responsible party
      </label>
      <select
        id="to-owner"
        className="input"
        value={toId}
        onChange={(e) => setToId(e.target.value)}
        disabled={busy}
      >
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>

      <ul className="bgConseqList">
        <li>{newOwner?.label ?? 'They'} become the responsible party.</li>
        <li>Shards are untouched — no re-encryption, no re-deal.</li>
        <li>You and {newOwner?.label ?? 'they'} are both notified, and it's recorded in the audit trail.</li>
      </ul>

      <label className="bgAck">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} />
        <span>
          I understand this changes accountability only — it does not give{' '}
          {newOwner?.label ?? 'them'} the ability to read the secret.
        </span>
      </label>

      <p className="bgDialogNote">
        Need someone to be able to read the secret instead? That's a re-deal, not a transfer.
      </p>

      {error && <FeedbackMessage ok={false} msg={error} />}
    </Modal>
  );
}

export default TransferOwnershipDialog;
